<div class="flex items-center justify-center p-4 sm:p-8 w-full min-h-screen">
  <div class="card pb-6 pt-8 sm:pt-10 px-6 sm:px-10 max-w-[440px] w-full">
    <div class="flex justify-center mb-3">
      <div style="height: 48px; max-width: 220px; display: flex; align-items: center; justify-content: center;">
        <img
          [src]="tenantBrandingService.logoUrl() || 'assets/creatives/logo-w70.png'"
          (error)="onLogoError($event)"
          class="signin-logo"
          style="max-height: 100%; max-width: 100%; width: auto; height: auto; object-fit: contain;"
          [alt]="tenantBrandingService.appName() + ' logo'"
        />
      </div>
    </div>

    <!-- STEP 1: Pre-auth confirmation -->
    @if (!inviteAccepted() && inviteInfo()) {
      <div class="invite-confirmation animate-fade-in-up">
        <p class="text-xl font-bold text-[#1D2433] mb-1 text-center">{{ 'Sessions.Signin.YouveBeenInvited' | translate }}</p>
        <p class="text-sm text-[#5B6372] mb-5 text-center">{{ 'Sessions.Signin.InviteConfirmSubtitle' | translate }}</p>

        <div class="invite-details-card mb-4">
          <div class="invite-detail-row">
            <span class="invite-detail-label">{{ 'Sessions.Signin.Workspace' | translate }}</span>
            <span class="invite-detail-value font-semibold">{{ inviteInfo()?.workspaceName }}</span>
          </div>
          <div class="invite-detail-row">
            <span class="invite-detail-label">{{ 'Sessions.Signin.Role' | translate }}</span>
            <span class="invite-detail-value">
              <span class="role-badge">{{ 'Roles.' + (inviteInfo()?.role | titlecase) | translate }}</span>
            </span>
          </div>
          <div class="invite-detail-row">
            <span class="invite-detail-label">{{ 'Sessions.Signin.InvitedByLabel' | translate }}</span>
            <span class="invite-detail-value">{{ inviteInfo()?.inviterName }}</span>
          </div>
        </div>

        <button mat-flat-button color="primary" class="w-full mb-2" (click)="acceptAndContinue()">
          {{ 'Sessions.Signin.AcceptAndSignIn' | translate }}
        </button>

        <button mat-button class="w-full text-sm text-gray-500" (click)="declineInvite()">
          {{ 'Sessions.Signin.DeclineInvite' | translate }}
        </button>
      </div>
    }

    <!-- Loading state while verifying -->
    @if (!inviteAccepted() && !inviteInfo() && !inviteError()) {
      <p class="text-xl font-bold text-[#1D2433] mb-2 text-center">{{ 'Sessions.Signin.Invitation' | translate }}</p>
      <p class="text-sm text-[#5B6372] mb-6 text-center">{{ 'Sessions.Signin.VerifyingInvitation' | translate }}</p>
      <!-- <mat-progress-bar mode="indeterminate" class="rounded-full"></mat-progress-bar> -->
    }

    <!-- Error state: invite not found / expired -->
    @if (inviteError()) {
      <div class="text-center animate-fade-in-up">
        <p class="text-xl font-bold text-[#1D2433] mb-2">{{ 'Sessions.Signin.Invitation' | translate }}</p>
        <div class="flex items-center gap-2 bg-red-50 text-red-700 rounded-lg px-3 py-3 mb-6">
          <span class="text-sm font-medium">{{ 'Sessions.Signin.InviteNotFound' | translate }}</span>
        </div>
        <button mat-flat-button color="primary" class="w-full" routerLink="/login">
          {{ 'Sessions.Login.BackToSignIn' | translate }}
        </button>
      </div>
    }

    <!-- STEP 2: Auth form (shown after user confirms acceptance) -->
    @if (inviteAccepted()) {
      @if (alreadyLoggedIn()) {
        <div class="flex flex-col items-center animate-fade-in-up mt-2">
          <p class="text-sm text-[#5B6372] mb-6 text-center">
            {{ 'Sessions.Signin.LoggedInAsPrefix' | translate }}<br/>
            <b>{{ currentUserEmail() }}</b>
          </p>
          
          <button mat-flat-button color="primary" class="w-full mb-3 h-[42px]" [disabled]="loading()" (click)="acceptInviteDirectly()">
            {{ 'Sessions.Signin.AcceptAndSignIn' | translate }}
          </button>
          <button mat-button class="w-full text-gray-500" [disabled]="loading()" (click)="declineInvite()">
            {{ 'Sessions.Signin.DeclineInvite' | translate }}
          </button>
        </div>
      } @else {
        <div class="flex items-center gap-2 bg-blue-50 text-blue-700 rounded-lg px-3 py-2 mb-6 font-medium animate-fade-in-up">
          <span class="text-sm font-semibold">{{ 'Sessions.Signin.InvitationAcceptedContinue' | translate }}</span>
        </div>

        <form [formGroup]="signinForm" class="flex flex-col text-left w-full">
        <div class="w-full mb-3">
          <mat-label class="form-label mb-1">{{ 'Sessions.Login.EmailField' | translate }}</mat-label>
          <mat-form-field subscriptSizing="dynamic" appearance="fill" class="soft-input w-full">
            <input id="email" matInput formControlName="email" type="email" name="email" autocomplete="username" [placeholder]="'Sessions.Login.EmailHere' | translate" />
          </mat-form-field>
        </div>

        @if (isNewUser()) {
          <div class="w-full mb-3">
            <mat-label class="form-label mb-1">{{ 'Sessions.Signin.FullNameField' | translate }}</mat-label>
            <mat-form-field subscriptSizing="dynamic" appearance="fill" class="soft-input w-full">
              <input id="fullName" matInput formControlName="fullName" type="text" name="fullName" autocomplete="name" [placeholder]="'Sessions.Signin.FullNamePlaceholder' | translate" />
            </mat-form-field>
          </div>
        }

        <div class="w-full mb-2">
          <mat-label class="form-label mb-1">{{ (isNewUser() ? 'Sessions.Login.CreatePasswordField' : 'Sessions.Login.PasswordField') | translate }}</mat-label>
          <mat-form-field subscriptSizing="dynamic" appearance="fill" class="soft-input w-full">
            <input
              id="password"
              matInput
              formControlName="password"
              [type]="hidePassword() ? 'password' : 'text'"
              name="password"
              autocomplete="current-password"
              [placeholder]="(isNewUser() ? 'Sessions.Signup.PasswordPlaceholder' : 'Sessions.Login.PasswordPlaceholder') | translate"
              (keydown.enter)="onSubmit()"
            />
            <button mat-icon-button matSuffix type="button" (click)="toggleHide()" [attr.aria-label]="'Hide password'">
              <mat-icon>{{ hidePassword() ? 'visibility_off' : 'visibility' }}</mat-icon>
            </button>
          </mat-form-field>
        </div>

        @if (isNewUser()) {
          <div class="w-full mb-2">
            <mat-label class="form-label mb-1">{{ 'Sessions.Login.ConfirmPasswordField' | translate }}</mat-label>
            <mat-form-field subscriptSizing="dynamic" appearance="fill" class="soft-input w-full">
              <input
                id="confirmPassword"
                matInput
                formControlName="confirmPassword"
                [type]="hideConfirmPassword() ? 'password' : 'text'"
                name="confirmPassword"
                autocomplete="new-password"
                [placeholder]="'Sessions.Login.ConfirmPasswordPlaceholder' | translate"
                (keydown.enter)="onSubmit()"
              />
              <button mat-icon-button matSuffix type="button" (click)="hideConfirmPassword.update(v => !v)" [attr.aria-label]="'Hide password'">
                <mat-icon>{{ hideConfirmPassword() ? 'visibility_off' : 'visibility' }}</mat-icon>
              </button>
            </mat-form-field>
            @if (passwordMismatch) {
              <p class="text-xs text-red-500 mt-1 ml-1">{{ 'Sessions.Login.PasswordMismatch' | translate }}</p>
            }
          </div>
        }

        <div class="mb-3"></div>

        <button
          class="w-full py-1 text-sm rounded-lg font-medium mb-1"
          (click)="onSubmit()"
          [disabled]="
            loading() ||
            passwordMismatch ||
            (isNewUser() ? signinForm.get('password')?.invalid || !signinForm.get('confirmPassword')?.value || !signinForm.get('fullName')?.value?.trim() : signinForm.invalid)
          "
          mat-flat-button
          color="primary"
        >
          {{ 'Sessions.Signin.SignInAndJoin' | translate }}
        </button>

        <!-- Or divider -->
        <div class="or-divider flex items-center gap-3 my-3">
          <div class="flex-1 h-px bg-gray-200"></div>
          <span class="text-[13px] text-gray-400 whitespace-nowrap">{{ 'Sessions.Login.OrContinueWith' | translate }}</span>
          <div class="flex-1 h-px bg-gray-200"></div>
        </div>

        @if (isNewUser()) {
          <p class="text-xs text-center text-gray-500 mb-3">{{ 'Sessions.Login.SkipPasswordHint' | translate }}</p>
        }

        <div class="flex flex-col gap-2">
          <button
            matButton="tonal"
            class="provider-login w-full border border-gray-200 rounded-lg justify-center flex items-center h-[42px]"
            [disabled]="loading()"
            (click)="signinWithProvider('google')"
          >
            <img src="assets/images/google.svg" class="h-4 mr-2" />
            <span>{{ 'Sessions.Signin.ContinueWithGoogle' | translate }}</span>
          </button>

          <button
            matButton="tonal"
            class="provider-login w-full border border-gray-200 rounded-lg justify-center flex items-center h-[42px]"
            [disabled]="loading()"
            (click)="signinWithProvider('microsoft')"
          >
            <img src="assets/images/microsoft.svg" class="h-4 mr-2" />
            <span>{{ 'Sessions.Signin.ContinueWithMicrosoft' | translate }}</span>
          </button>
        </div>
        </form>
      }
    }

    <div class="mt-4">
      @if (loading()) {
        <mat-progress-bar mode="indeterminate" class="rounded-full"></mat-progress-bar>
      }
    </div>

    <app-language-selector class="mt-6 flex justify-center w-full" />

    <!-- Trust footer -->
    <div class="flex items-center justify-center gap-1.5 mt-6 text-gray-400 text-xs">
      <mat-icon class="text-[14px]! h-3.5! w-3.5!">lock</mat-icon>
      <span>{{ 'Sessions.Login.SecureConnection' | translate }}</span>
    </div>
  </div>
</div>
